import * as provider from "@mdx-js/react";
import * as runtime from "react/jsx-runtime";
import dayjs from "dayjs";

import {
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect,
  useState,
  Fragment,
  Children,
  HTMLProps,
  ReactElement,
  ReactNode,
  isValidElement,
} from "react";

import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import remarkFlexibleToc, { TocItem } from "remark-flexible-toc";
import remarkGfm from "remark-gfm";
import { evaluateSync, EvaluateOptions } from "@mdx-js/mdx";
import { ThemeProvider } from "styled-components";
import { t } from "@lingui/core/macro";

import { getTheme } from "_/style";
import { docsUrls } from "_/routes";
import { useIsAonUser } from "_/data/users";
import {
  LayoutNode,
  DocMeta,
  usePrefetchDoc,
  useDocPage,
  useDocsManifest,
} from "_/data/docs";
import { Icon } from "_/components/icon";
import {
  Link,
  Redirect,
  useHashLocation,
  useParams,
} from "_/components/router";
import PrintSandbox from "_/utils/print-sandbox";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  ThCell,
  Td,
  TableContainer,
} from "_/components/table/styled";
import { Button } from "_/components/button";
import { Drawer } from "_/components/drawer";

import * as S from "./styled";
import { Code } from "./code";
import { Contents } from "./contents";
import { DocsLink } from "./docs-link";
import { DocsImg } from "./docs-img";
import { DocsContext } from "./context";
import { FileDownload } from "./file-download";
import { Notice } from "./notice";
import { Tabs, Tab } from "./tabs";

/**
 * The `<thead>` element the MDX runtime passes as a table's first child.
 * Its own child is the header `<tr>`, whose children are the `<th>` cells,
 * a nesting (thead > tr > th) not captured by `ReactElement`. The cells are
 * a `ReactNode` rather than an array because a single-column table passes
 * one child instead of a list.
 */
type TableHeaderElement = ReactElement<{
  children: ReactElement<{ children: ReactNode }>;
}>;

/**
 * Narrows MDX table children to an element array whose first element is
 * the header-carrying `<thead>`. `every(isValidElement)` only proves the
 * elements are `ReactElement`s; the header shape on the first element is
 * asserted here based on how the runtime lays out tables.
 */
const isTableElementArray = (
  children: ReactNode
): children is [TableHeaderElement, ...ReactElement[]] =>
  Array.isArray(children) && children.every(isValidElement);

/**
 * Narrows MDX children to a `ReactElement[]`. `Array.isArray` alone only
 * narrows to `any[]`, so we also confirm every entry is an element.
 */
const isElementArray = (children: ReactNode): children is ReactElement[] =>
  Array.isArray(children) && children.every(isValidElement);

/**
 * Detects a table header row by its first cell. MDX overrides `th` with a
 * component whose inferred function name is `"th"`, so a header cell's
 * `type` is that function. `ReactNode` doesn't expose `type`, so we narrow
 * to an element first, then read the optional `name` off its `type`.
 */
const isHeaderCell = (node: ReactNode): boolean =>
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  isValidElement(node) && (node.type as { name?: string }).name === "th";

/**
 * Components to be used in the MDX content.
 *
 * These will override the "default" tags used in the MDX and allow for
 * use of custom react components within the MDX as well (e.g. the `<Notice />`
 * component).
 */
const MDX_COMPONENTS = {
  a: DocsLink,
  hr: S.DocsHr,
  code: Code,
  Notice,
  Contents,
  FileDownload,
  Tabs,
  Tab,
  img: DocsImg,
  table: ({ children }: HTMLProps<HTMLTableElement>) => {
    if (!isTableElementArray(children)) {
      console.error("Table component must have children array.");
      return null;
    }

    // The `<Table />` component requires the columnCount to be provided in
    // order to render the correct column layout using flex-grid. This is a
    // somewhat inelegant way to determine the number of columns, but is
    // required due to how markdown tables are computed before being passed
    // to the custom table component here. `Children.count` handles both the
    // multi-column (array) and single-column (lone child) cases.
    const columnCount = Children.count(
      children[0].props.children.props.children
    );

    return (
      <TableContainer style={{ margin: "20px 0" }}>
        <Table $columnCount={columnCount} role="grid">
          {children}
        </Table>
      </TableContainer>
    );
  },
  tbody: Tbody,
  thead: Thead,
  th: ({ children }: HTMLProps<HTMLTableCellElement>) => (
    <Th role="columnheader">
      <ThCell>{children}</ThCell>
    </Th>
  ),
  tr: ({ children }: HTMLProps<HTMLTableRowElement>) => {
    const childrenArray = isElementArray(children) ? children : [children];

    if (isHeaderCell(childrenArray[0])) {
      return (
        <Tr role="row">
          {childrenArray}
          <Th />
        </Tr>
      );
    }

    return (
      <Tr role="row">
        {childrenArray}
        <Td role="gridcell" />
      </Tr>
    );
  },
  td: ({ children }: HTMLProps<HTMLTableCellElement>) => (
    <Td role="gridcell">{children}</Td>
  ),
};

// Format the breadcrumb path based on the current slug
export function formatBreadcrumb(props: {
  currentSlug: string | undefined;
  slugs: Record<string, { title: string }>;
}): ReactNode {
  const { currentSlug, slugs } = props;
  if (!currentSlug) return t`common.documentation`;

  const clean = (s: string) =>
    s
      .replaceAll(/^['"]|['"]$/g, "")
      .replaceAll(/\/+$/g, "")
      .replace(/^\/+/, "");

  const parts = clean(currentSlug).split("/").filter(Boolean);
  const crumbs = parts.map((_, i) => parts.slice(0, i + 1).join("/"));

  return (
    <>
      {crumbs.map((slug, i) => {
        const title = slugs[slug]?.title;
        const href = docsUrls.page(slug);
        const isLast = i === crumbs.length - 1;
        return (
          <Fragment key={slug}>
            {isLast ? (
              <span aria-current="page">{title}</span>
            ) : (
              <Link to={href}>{title}</Link>
            )}
            {!isLast && <span aria-hidden="true">{" > "}</span>}
          </Fragment>
        );
      })}
    </>
  );
}

function flattenLayout(layout: LayoutNode[]): string[] {
  const flattenedOutput: string[] = [];
  const walk = (nodes: LayoutNode[]) => {
    for (const n of nodes) {
      if (n.slug) flattenedOutput.push(n.slug);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(layout);
  return flattenedOutput;
}

// Resolves once an image finishes loading or errors, whichever comes first.
function waitForImage(img: HTMLImageElement): Promise<void> {
  return new Promise((resolve) => {
    const onSettled = () => {
      img.removeEventListener("load", onSettled);
      img.removeEventListener("error", onSettled);
      resolve();
    };
    img.addEventListener("load", onSettled);
    img.addEventListener("error", onSettled);
  });
}

// Images without a reserved size can still be loading when the target
// heading first appears, and shift the page once they finish loading. Wait
// for them so we don't scroll before the page has settled.
function waitForPendingImages(): Promise<void> {
  const pending = [...document.querySelectorAll("img")].filter(
    (img) => !img.complete
  );
  if (pending.length === 0) {
    return Promise.resolve();
  }

  return Promise.all(pending.map(waitForImage)).then(() => undefined);
}

/**
 * Main top level documentation view component.
 */
export const DocsView = (): ReactElement => {
  const params = useParams();
  const manifestQuery = useDocsManifest();
  const [hashLoc, _] = useHashLocation();
  const [scrolledToTarget, setScrolledToTarget] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const order = useMemo(() => {
    if (manifestQuery.data) {
      return flattenLayout(manifestQuery.data.layout);
    }
    return [];
  }, [manifestQuery.data]);

  // Allow for modifying the page titles and reset to the previous title when
  // the docs page component is unmounted.

  useLayoutEffect(() => {
    const originalTitle = document.title;
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // Scroll to any specified hash locations in the URL when the page loads.
  // This is necessary because the mdx content is rendered _after_ the pageload.
  useLayoutEffect(() => {
    if (scrolledToTarget) {
      return undefined;
    }

    // The hash location provided by wouter includes a leading slash
    // which must be removed.
    const hash = hashLoc.replace(/^\//, "");

    if (!hash) {
      return undefined;
    }

    // `getElementById` is actually correct here because `querySelector` throws
    // if the hash isn't a valid CSS selector (e.g. if it starts with a number).
    // This hash comes from untrusted input so we don't want to allow it to
    // crash the browser.
    // oxlint-disable-next-line unicorn/prefer-query-selector
    const findTarget = () => document.getElementById(hash);

    let cancelled = false;

    const scrollTo = (el: HTMLElement) => {
      void waitForPendingImages().then(() => {
        if (!cancelled) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          setScrolledToTarget(true);
        }
      });
    };

    // The target may already be present, e.g. when navigating to a page
    // whose content is already cached.
    const existingTarget = findTarget();
    if (existingTarget) {
      scrollTo(existingTarget);
      return () => {
        cancelled = true;
      };
    }

    const observer = new MutationObserver(() => {
      const target = findTarget();
      if (target) {
        observer.disconnect();
        scrollTo(target);
      }
    });

    observer.observe(document, { subtree: true, childList: true });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [hashLoc, scrolledToTarget, setScrolledToTarget]);

  if (manifestQuery.isLoading) {
    return (
      <S.LoadingWrapper>
        <S.LoadingSpinner />
      </S.LoadingWrapper>
    );
  }

  if (!manifestQuery.data) {
    return <>404: Documentation manifest not found</>;
  }

  const { layout, slugs } = manifestQuery.data;
  const currentSlug = params.slug ?? "index";
  const idx = order.indexOf(currentSlug);
  const prevSlug = order[idx - 1];
  const nextSlug = order[idx + 1];
  const docContext = { manifest: manifestQuery.data, currentSlug };

  return (
    <DocsContext.Provider value={docContext}>
      <S.MobileTopBar>
        <Button
          iconOnly
          kind="secondary"
          variant="text"
          onClick={() => setNavOpen((v) => !v)}
        >
          <Icon variant="Menu" />
        </Button>
        <div>{formatBreadcrumb({ currentSlug: params.slug, slugs })}</div>
      </S.MobileTopBar>

      <S.Wrapper>
        <Drawer
          open={navOpen}
          onClose={() => setNavOpen(false)}
          width={300}
          side="left"
          desktopMode="inline"
          style={{ padding: "1.25rem 1.25rem 1.25rem 2.5rem" }}
        >
          <h3>{t`common.documentation`}</h3>
          {/* Clicking any nav item on mobile will close the drawer */}
          <S.DocsNavList onClick={() => setNavOpen(false)}>
            {layout.map((itm) => (
              <NavItem
                key={itm.slug}
                item={itm}
                depth={0}
                currentSlug={currentSlug}
              />
            ))}
          </S.DocsNavList>
        </Drawer>

        {/* Main content + right TOC */}
        <DocsContent slug={params.slug} />

        <S.PageArrows>
          {prevSlug && (
            <S.PageArrowLeft>
              <Link to={docsUrls.page(prevSlug)}>
                <Icon variant="ChevronLeft" />
              </Link>
            </S.PageArrowLeft>
          )}
          {nextSlug && (
            <S.PageArrowRight>
              <Link to={docsUrls.page(nextSlug)}>
                <Icon variant="ChevronRight" />
              </Link>
            </S.PageArrowRight>
          )}
        </S.PageArrows>
      </S.Wrapper>
    </DocsContext.Provider>
  );
};

const DocsContent = ({ slug }: { slug?: string }): ReactElement => {
  const pageQuery = useDocPage(slug);

  if (pageQuery.isLoading) {
    return (
      <S.LoadingWrapper>
        <S.LoadingSpinner />
      </S.LoadingWrapper>
    );
  }

  if (!pageQuery.data) {
    console.error(`No page data loaded for doc. Check the slug "${slug}".`);
    return <>404: Documentation page not found</>;
  }

  const { content, meta } = pageQuery.data;

  if (slug !== meta.slug) {
    // This happens when we request an alias. The server will return the meta object
    // of the object pointed to by the alias. To keep everything consistent, we
    // simply redirect to that original page.
    return <Redirect to={docsUrls.page(meta.slug)} />;
  }

  return <MdxContent content={content} meta={meta} />;
};

// Initial depth to open the nav items to.
const INITIAL_DEPTH = 1;

function containsCurrentPage(item: LayoutNode, currentSlug: string): boolean {
  return (
    item.slug === currentSlug ||
    item.children.some((child) => containsCurrentPage(child, currentSlug))
  );
}

/**
 * Helper component for creating and nesting the nav items.
 *
 * The `<NavItem />` component is recursive and will render itself for
 * each entry to be displayed in the navigation, nesting deeper for each
 * level in the docs manifest.
 */
const NavItem = ({
  item,
  depth,
  currentSlug,
}: {
  item: LayoutNode;
  depth: number;
  currentSlug: string;
}): ReactElement => {
  const prefetchDoc = usePrefetchDoc();
  const [isHovered, setIsHovered] = useState(false);
  const containsActivePage = containsCurrentPage(item, currentSlug);
  const [isExpanded, setIsExpanded] = useState(
    depth < INITIAL_DEPTH || containsActivePage
  );
  const [previousCurrentSlug, setPreviousCurrentSlug] = useState(currentSlug);
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const route = docsUrls.page(item.slug);
  const match = item.slug === currentSlug;

  // A nested page can be reached directly or after client-side navigation.
  // Keep each ancestor open before committing the navigation's new sidebar.
  if (currentSlug !== previousCurrentSlug) {
    setPreviousCurrentSlug(currentSlug);
    if (containsActivePage) {
      setIsExpanded(true);
    }
  }

  // Debounce preloading so that mousing over the nav doesn't load all the
  // content unless there's a more definite intent to click an item.
  useEffect(() => {
    if (isHovered && !timeoutId) {
      const id = setTimeout(() => {
        void prefetchDoc(item.slug);
      }, 50);
      setTimeoutId(id);
    } else if (!isHovered && timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  }, [item.slug, isHovered, prefetchDoc, timeoutId]);

  const items = item.children?.map((itm) => (
    <NavItem
      key={itm.slug}
      item={itm}
      depth={depth + 1}
      currentSlug={currentSlug}
    />
  ));

  const expandArrow =
    items.length > 0 ? (
      <S.DocsNavItemArrow
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        $expanded={isExpanded}
      />
    ) : null;

  return (
    <Fragment key={item.slug}>
      <S.DocsNavItem
        onClick={() => setIsExpanded(!(match && isExpanded))}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        $depth={depth}
        $active={match}
        $parent={items.length > 0}
      >
        {expandArrow}
        <Link to={route}>{item.title}</Link>
      </S.DocsNavItem>
      {isExpanded ? items : null}
    </Fragment>
  );
};

const MdxContent = (props: {
  content: string;
  meta: DocMeta;
}): ReactElement => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Set the document title and scroll to the top of the page when the content
  // or metadata changes.
  useLayoutEffect(() => {
    document.title = props.meta.title || t`common.documentation`;
    contentRef.current?.scrollTo(0, 0);
  }, [props.content, props.meta]);

  // MDX file table of contents is parsed and stored in this array
  // by the `remarkFlexibleToc` plugin. It must be an empty array
  // and will be filled when `evaluateSync` is run.
  const tocRef = useRef<TocItem[]>([]);

  // Content component must be memoized to avoid re-rendering the document tree.
  // This is necessary because the `evaluateSync` function is called on every
  // render regardless of changes to `props.content`.
  const { default: Content } = useMemo(() => {
    const options = {
      ...provider,
      ...runtime,
      remarkPlugins: [
        remarkGfm,
        [remarkFlexibleToc, { tocRef: tocRef.current }],
      ],
      rehypePlugins: [
        rehypeSlug,
        [rehypeAutolinkHeadings, { behavior: "wrap" }],
      ],
      useMDXComponents: () => MDX_COMPONENTS,
    } as EvaluateOptions;

    return evaluateSync(props.content, options);
  }, [props.content, tocRef]);

  const modifiedAt = dayjs(props.meta.modifiedAt);
  const theme = getTheme("light");

  return (
    <>
      <PrintSandbox selector=".mainContent" />
      <S.MdxContentWrapper>
        <S.MainColumn>
          {props.meta.draft && (
            <S.DraftRibbon>{t`docs-view.draft`}</S.DraftRibbon>
          )}
          <ThemeProvider theme={theme}>
            <S.MainContent ref={contentRef} className="mainContent">
              {props.meta.missingTranslation && (
                <Notice kind="notice">{t`docs-view.missing-translation-notice`}</Notice>
              )}
              <h1>{props.meta.title} </h1>
              <Content />
            </S.MainContent>
          </ThemeProvider>
        </S.MainColumn>

        <S.TocContent>
          <Button size="medium" onClick={() => window.print()}>
            <Icon variant="LocalPrintshop" />
            {t`docs-view.print-this-page`}
          </Button>
          <h4>{t`docs-view.page-toc`}</h4>
          <PageToc toc={tocRef.current} />
          <S.ModifiedAt>{t`docs.last-modified ${modifiedAt.fromNow()}`}</S.ModifiedAt>
          <EditLink source={props.meta.path} />
        </S.TocContent>
      </S.MdxContentWrapper>
    </>
  );
};

const EditLink = ({ source }: { source: string }): ReactElement => {
  const isAonUser = useIsAonUser();

  if (!isAonUser) {
    return <></>;
  }

  const sourcePath = `https://github.com/aon3d/docs/blob/master/src/${source}`;

  return (
    <S.EditLink href={sourcePath} target="_blank">
      Edit this file
      <Icon variant="Edit" />
    </S.EditLink>
  );
};

/**
 * Document table of contents displayed on the right side of the page.
 */
const PageToc = ({ toc }: { toc: TocItem[] }): ReactElement => {
  // Calculate the minimum depth of the TOC items to use for the display
  // indentation level. Depth may vary depending on the headings used so can
  // sometimes start at 1, and other times start at 2 or 3.
  const depths = toc.map((item) => item.depth);
  const minDepth = Math.min(...depths);

  const tocItems = toc.map((item, i) => (
    <S.PageTocItem key={`${i}-${item.value}`} $depth={item.depth - minDepth}>
      <a href={item.href}>{item.value}</a>
    </S.PageTocItem>
  ));

  return <S.PageToc>{tocItems}</S.PageToc>;
};
